import React, { useCallback } from 'react'
import {
  ColumnDef,
  ColumnPinningState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table'
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Pagination,
  PaginationItem,
  Grid,
  Typography,
  Skeleton,
  TableSortLabel,
} from '@mui/material'
import times from 'lodash/times'
import { TableSortLabelOwnProps } from '@mui/material/TableSortLabel/TableSortLabel'

import { Icons } from '../../assets/icons'
import { ButtonItem, ButtonSelect } from '../button-select.tsx'
import { TableSort } from '../../stores'

import { TableBodyRenderer } from './table-body-renderer.tsx'
import { getCommonPinningStyles } from './utils.ts'

export interface TableProps<T extends Record<string, any>> {
  data: Array<T>
  columns: Array<ColumnDef<T, any>>
  rowKey: keyof T
  isLoading?: boolean

  page?: number
  size?: number
  totalPages?: number
  sort?: TableSort | []
  columnPinning?: ColumnPinningState

  onSizeChange?(size: number): void
  onPageChange?(page: number): void
  onRowClick?(row: T): () => void
  onSort?(sort?: TableSort): void
}

export const TableComponent = <T extends Record<string, any>>({
  data,
  columns,
  rowKey,

  isLoading,
  size = 10,
  page = 0,
  totalPages,
  columnPinning = { left: [], right: [] },
  sort: [sortId, sortDirection] = [],

  onPageChange,
  onSizeChange,
  onRowClick,
  onSort,
}: TableProps<T>): React.ReactElement => {
  const table = useReactTable<T>({
    data,
    columns,
    initialState: {
      pagination: {
        pageIndex: page,
        pageSize: size,
      },
      columnPinning,
    },
    state: { sorting: [{ id: sortId as string, desc: sortDirection === 'DESC' }] },
    // Pipeline
    getRowId: (originalRow) => originalRow[rowKey],
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),

    debugTable: true,
    manualPagination: true,
    manualSorting: true,
  })

  const handleChangePage = useCallback(
    (_: unknown, page: number) => {
      if (!onPageChange) return

      onPageChange(page - 1)
      window.scrollTo({ top: 0 })
    },
    [onPageChange]
  )

  const handleChangeSize = useCallback(
    (item: ButtonItem<number>) => {
      if (!onSizeChange) return

      onSizeChange(item.value)
      setTimeout(() => window.scrollTo({ top: 0 }), 0)
    },
    [onSizeChange]
  )

  const handleSort = useCallback(
    (key: string) => () => {
      if (!onSort) return

      const isAsc = key === sortId && sortDirection === 'ASC'
      const isDesc = key === sortId && sortDirection === 'DESC'

      onSort(isDesc ? undefined : [key, isAsc ? 'DESC' : 'ASC'])
    },
    [onSort, sortId, sortDirection]
  )

  const hasPagination = Boolean(onSizeChange || onPageChange) && !!data.length

  return (
    <>
      <TableContainer
        sx={{
          border: '1px solid',
          borderColor: 'lines.grey',
          borderRadius: 2,
        }}
      >
        <Table>
          <TableHead>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableCell key={header.id} colSpan={header.colSpan} sx={getCommonPinningStyles(header.column, true)}>
                    {header.column.getCanSort() ? (
                      <TableSortLabel
                        onClick={handleSort(header.id)}
                        active={sortId === header.id}
                        direction={
                          sortId === header.id && sortDirection
                            ? (sortDirection.toLowerCase() as TableSortLabelOwnProps['direction'])
                            : 'asc'
                        }
                        sx={{ fontWeight: 500 }}
                      >
                        <Typography variant="caption" fontWeight="500">
                          {flexRender(header.column.columnDef.header, header.getContext())}
                        </Typography>
                      </TableSortLabel>
                    ) : (
                      <Typography variant="caption" fontWeight="500">
                        {flexRender(header.column.columnDef.header, header.getContext())}
                      </Typography>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableHead>
          <TableBody>
            {isLoading ? (
              times(size, (idx) => (
                <TableRow key={`loadingRow_${idx}`}>
                  <TableCell
                    colSpan={columns.length - (columnPinning?.left?.length ?? 0) - (columnPinning?.right?.length ?? 0)}
                  >
                    <Skeleton variant="rounded" width="100%" height="40px" />
                  </TableCell>
                  {!!columnPinning?.right?.length && (
                    <TableCell>
                      <Skeleton variant="rounded" width={40} height={40} />
                    </TableCell>
                  )}
                </TableRow>
              ))
            ) : (
              <TableBodyRenderer<T> rowModel={table.getRowModel()} onRowClick={onRowClick} />
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {hasPagination && (
        <Grid container flexDirection="row" mt={2} justifyContent="flex-end" spacing={2} alignItems="center">
          {!!onPageChange && (
            <Grid item>
              <Pagination
                renderItem={(item) => (
                  <PaginationItem
                    slots={{
                      previous: Icons.ArrowLeft as React.ElementType,
                      next: Icons.ArrowRight as React.ElementType,
                    }}
                    {...item}
                  />
                )}
                shape="rounded"
                color="primary"
                count={totalPages}
                page={page + 1}
                onChange={handleChangePage}
              />
            </Grid>
          )}
          {!!onSizeChange && (
            <Grid item>
              <ButtonSelect<number>
                id="table_size"
                onClick={handleChangeSize}
                value={size}
                items={[
                  { label: '10', value: 10 },
                  { label: '20', value: 20 },
                  { label: '50', value: 50 },
                ]}
              />
            </Grid>
          )}
        </Grid>
      )}
    </>
  )
}
